import { i18n } from '@/vue';
import { isNil } from 'lodash';
import { TableField, CellStyleFunction } from '../TableWrapper.types';
import {
  ACTION_ICON_COLUMN_WIDTH,
  EXPANDER_COLUMN_WIDTH,
  SELECTION_COLUMN_WIDTH,
} from '../constants';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';

export enum TableClass {
  ALIGN_MIDDLE = 'align-middle',
  BREAK_ALL_WORDS = 'break-all-words',
  TEXT_CENTER = 'text-center',
}
export type FieldOptions<T> = {
  labelTranslationKey?: string;
  valueTranslationKey?: string;
  label?: string;
  icon?: IconDefinition;
  sortable?: boolean;
  thClass?: TableClass[];
  tdClass?: (TableClass | string)[];
  cellClassGetter?: CellStyleFunction<T>;
  index?: number;
  width?: string;
  valueGetter?: (item: T) => (typeof item)[keyof T];
  truncation?: {
    maxLength: number;
  };
  isVisible?: boolean;
};
export type MinimalFieldOptions<T> = FieldOptions<T> &
  Required<Pick<FieldOptions<T>, 'sortable' | 'tdClass'>>;
export type TableOptions = {
  actionIcon?: boolean;
  expandableRows?: boolean;
  selection?: boolean;
};

export class TableFieldsBuilder<T extends object> {
  private readonly defaultOptions: MinimalFieldOptions<T>;
  private fields: TableField<T>[] = [];
  constructor(
    readonly defaultFieldOptions: FieldOptions<T> = {},
    private readonly tableOptions: TableOptions = {},
  ) {
    this.defaultOptions = {
      cellClassGetter: () => [],
      sortable: false,
      isVisible: true,
      thClass: [],
      tdClass: [],
      ...defaultFieldOptions,
    };
  }

  public addField(key: keyof T, fieldOptions: FieldOptions<T> = {}): this {
    const mergedOptions = this.getMergedOptions(fieldOptions);
    const field: TableField<T> = {
      key: key,
      ...this.getLabelOrIcon(key.toString(), fieldOptions),
      sortable: mergedOptions.sortable,
      tdClass: mergedOptions.tdClass,
      cellClassGetter: mergedOptions.cellClassGetter,
      valueTranslationKey: mergedOptions.valueTranslationKey,
      valueGetter: mergedOptions.valueGetter,
      width: mergedOptions.width,
      truncation: mergedOptions.truncation,
      isVisible: mergedOptions.isVisible,
    };

    if (mergedOptions.index !== undefined) {
      this.fields.splice(mergedOptions.index, 0, field);
      return this;
    } else this.fields.push(field);

    return this;
  }

  private getMergedOptions(
    fieldOptions: FieldOptions<T>,
  ): MinimalFieldOptions<T> {
    return {
      ...this.defaultOptions,
      ...fieldOptions,
    };
  }

  private getLabelOrIcon(
    key: string,
    options: FieldOptions<T>,
  ): { label: string } | { icon: IconDefinition } {
    switch (true) {
      case !isNil(options.label):
        return { label: options.label! };
      case !isNil(options.icon):
        return { icon: options.icon! };
      default:
        return {
          label: i18n.global.t(
            `${this.defaultOptions.labelTranslationKey}.${key}`,
          ),
        };
    }
  }

  public build(): TableField<T>[] {
    const visibleFields = this.fields.filter((field) => field.isVisible);
    const hasOnlyPercentWidths = visibleFields.every((field) =>
      field.width?.endsWith('%'),
    );
    const reservedWidth =
      (this.tableOptions.selection ? parseFloat(SELECTION_COLUMN_WIDTH) : 0) +
      (this.tableOptions.expandableRows
        ? parseFloat(EXPANDER_COLUMN_WIDTH)
        : 0) +
      (this.tableOptions.actionIcon ? parseFloat(ACTION_ICON_COLUMN_WIDTH) : 0);
    if (hasOnlyPercentWidths && reservedWidth > 0) {
      const totalVisibleWidth = visibleFields.reduce(
        (acc, field) => acc + parseFloat(field.width!),
        0,
      );
      const scaleFactor = (100 - reservedWidth) / totalVisibleWidth;
      for (const field of visibleFields) {
        const current = parseFloat(field.width!);
        field.width = `${(current * scaleFactor).toFixed(2)}%`;
      }
    }
    return visibleFields;
  }
}
